/**
 * SankeyRenderer — the Sankey / flow lens.
 *
 * Renders a horizontal Sankey diagram (value flow through a system) in a
 * selectable theme. Nodes are colored by cluster via the dynamic top-N palette
 * (rest neutral); links are drawn with sankeyLinkHorizontal(), stroked with a
 * soft blend toward their source-node color and an opacity scaled into a calm
 * band by flow value, with stroke-width = the sankey link width.
 *
 * This lens is a static layout: d3-sankey computes node/link geometry once,
 * so settle() is a no-op. All layout math is done arithmetically (no DOM
 * measurement) so the renderer runs headless under jsdom.
 *
 *   color  ← node.cluster  (dynamic 7-color palette, rest neutral)
 *   width  ← link.value    (sankey nodeWidth + computed link width)
 *   ink    ← link.value    (opacity band) + source-color blend
 */

import {
  sankey,
  sankeyLinkHorizontal,
  type SankeyGraph,
  type SankeyNode,
  type SankeyLink,
} from "d3-sankey";
import { scaleLinear } from "d3-scale";
import { select, type Selection } from "d3-selection";

import { THEMES, type Theme } from "../theme.js";
import type {
  LensExample,
  LensRenderer,
  LensRendererOptions,
} from "../lens.js";

// ── data shape (self-contained to this lens) ───────────────────────────────

interface SankeyDatum {
  title: string;
  nodes: { id: string; label: string; cluster?: string }[];
  links: { source: string; target: string; value: number }[];
}

interface SNodeExtra {
  id: string;
  label: string;
  cluster: string;
}
type SNode = SankeyNode<SNodeExtra, Record<string, unknown>>;
type SLink = SankeyLink<SNodeExtra, Record<string, unknown>>;

const SVG_NS = "http://www.w3.org/2000/svg";

export class SankeyRenderer implements LensRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private readonly showLabels: boolean;

  constructor(
    container: SVGSVGElement | HTMLElement,
    options: LensRendererOptions = {},
  ) {
    this.width = options.width ?? 960;
    this.height = options.height ?? 720;
    this.theme =
      typeof options.theme === "object"
        ? options.theme
        : THEMES[options.theme ?? "dark"];
    this.showLabels = options.showLabels ?? true;

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  render(data: unknown): this {
    const input = data as SankeyDatum;
    this.svg.selectAll("*").remove();

    // Background rect so exported SVGs carry the field with them.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", this.theme.background);

    const theme = this.theme;
    const margin = 48;
    const fs = theme.label.fontSize;

    // Build the sankey graph. Extent leaves room for labels at the frame edges.
    const sankeyGen = sankey<SNodeExtra, Record<string, unknown>>()
      .nodeId((d) => d.id)
      .nodeWidth(16)
      .nodePadding(18)
      .extent([
        [margin, margin],
        [this.width - margin, this.height - margin],
      ]);

    const nodes: SNode[] = input.nodes.map((n) => ({
      id: n.id,
      label: n.label,
      cluster: n.cluster ?? "default",
    }));
    const links: SLink[] = input.links.map((l) => ({
      source: l.source,
      target: l.target,
      value: l.value,
    }));

    const graph: SankeyGraph<SNodeExtra, Record<string, unknown>> = sankeyGen({
      nodes,
      links,
    });

    const colorOf = this.buildColorScale(graph.nodes);

    // Link opacity band scaled by value (calm range per theme).
    const values = graph.links.map((l) => l.value);
    const minV = Math.min(...values, 0);
    const maxV = Math.max(...values, 1);
    const [opMin, opMax] =
      theme.name === "editorial" ? [0.3, 0.6] : [0.25, 0.55];
    const opacityScale = scaleLinear()
      .domain([minV, maxV])
      .range([opMin, opMax])
      .clamp(true);

    const content = this.svg.append("g").attr("class", "content");

    // ── links ───────────────────────────────────────────────────────────
    const linkPath = sankeyLinkHorizontal<SNodeExtra, Record<string, unknown>>();
    const linkG = content.append("g").attr("class", "links").attr("fill", "none");
    linkG
      .selectAll<SVGPathElement, SLink>("path")
      .data(graph.links)
      .join("path")
      .attr("d", (d) => linkPath(d) ?? "")
      .attr("stroke", (d) => {
        const src = d.source as SNode;
        const base = colorOf(src.cluster);
        // Soft blend toward the field so links stay calm against the ground.
        return blend(base, theme.background, theme.name === "editorial" ? 0.25 : 0.4);
      })
      .attr("stroke-opacity", (d) => opacityScale(d.value))
      .attr("stroke-width", (d) => Math.max(1, d.width ?? 1));

    // ── node rects ───────────────────────────────────────────────────────
    const nodeG = content.append("g").attr("class", "nodes");
    nodeG
      .selectAll<SVGRectElement, SNode>("rect")
      .data(graph.nodes)
      .join("rect")
      .attr("x", (d) => d.x0 ?? 0)
      .attr("y", (d) => d.y0 ?? 0)
      .attr("width", (d) => Math.max(1, (d.x1 ?? 0) - (d.x0 ?? 0)))
      .attr("height", (d) => Math.max(1, (d.y1 ?? 0) - (d.y0 ?? 0)))
      .attr("fill", (d) => colorOf(d.cluster))
      .attr("fill-opacity", theme.node.opacity)
      .attr("stroke", theme.node.stroke ?? "none")
      .attr("stroke-width", theme.node.stroke ? theme.node.strokeWidth ?? 1 : 0);

    // ── labels ───────────────────────────────────────────────────────────
    if (this.showLabels) {
      const labelG = content.append("g").attr("class", "labels");
      const mid = this.width / 2;
      labelG
        .selectAll<SVGTextElement, SNode>("text")
        .data(graph.nodes)
        .join("text")
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", fs)
        .attr("fill", theme.label.color)
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 2.5)
        .attr("stroke-linejoin", "round")
        // Right of node in the left half; left of node in the right half.
        .attr("x", (d) => {
          const x0 = d.x0 ?? 0;
          const x1 = d.x1 ?? 0;
          return x0 < mid ? x1 + 6 : x0 - 6;
        })
        .attr("text-anchor", (d) => ((d.x0 ?? 0) < mid ? "start" : "end"))
        .attr("y", (d) => ((d.y0 ?? 0) + (d.y1 ?? 0)) / 2)
        .attr("dominant-baseline", "middle")
        .each((d, i, groups) => {
          const t = select(groups[i]);
          t.append("tspan").text(d.label);
          t.append("tspan")
            .attr("fill", theme.label.valueColor)
            .attr(
              "font-family",
              theme.label.valueFontFamily ?? theme.label.fontFamily,
            )
            .attr("dx", 4)
            .text(formatValue(d.value ?? 0));
        });
    }

    // Title in the top-left margin band.
    if (input.title) {
      content
        .append("text")
        .attr("x", margin)
        .attr("y", margin * 0.55)
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", fs + 4)
        .attr("font-weight", 600)
        .attr("fill", theme.label.color)
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 3)
        .attr("stroke-linejoin", "round")
        .text(input.title);
    }

    return this;
  }

  settle(_ticks?: number): this {
    // Static layout — sankey geometry is computed in render().
    return this;
  }

  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.svg.selectAll("*").remove();
  }

  // ── internals ──────────────────────────────────────────────────────────

  /** Dynamic palette: rank clusters by total node value, color top N, rest neutral. */
  private buildColorScale(nodes: SNode[]): (cluster: string) => string {
    const weightByCluster = new Map<string, number>();
    for (const n of nodes) {
      weightByCluster.set(
        n.cluster,
        (weightByCluster.get(n.cluster) ?? 0) + (n.value ?? 0),
      );
    }
    const ranked = [...weightByCluster.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, this.theme.maxColoredClusters)
      .map(([cluster]) => cluster);
    const clusterColor = new Map<string, string>();
    ranked.forEach((cluster, i) =>
      clusterColor.set(cluster, this.theme.palette[i]),
    );
    return (cluster: string) =>
      clusterColor.get(cluster) ?? this.theme.neutralCluster;
  }
}

// ── pure helpers ────────────────────────────────────────────────────────────

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return v < 1 ? `${Math.round(v * 100)}%` : v.toFixed(1);
}

/** Blend #rrggbb `a` toward #rrggbb `b` by t (0 = a, 1 = b) → rgb(). */
function blend(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `rgb(${mix(ca.r, cb.r)},${mix(ca.g, cb.g)},${mix(ca.b, cb.b)})`;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

// ── examples ─────────────────────────────────────────────────────────────────

export const SANKEY_EXAMPLES: LensExample[] = [
  {
    id: "sankey-content-flywheel",
    title: "Content Flywheel Throughput",
    blurb:
      "Raw sources flow through synthesis and review into published formats, with a stalled branch bleeding off value.",
    highlights: ["throughput", "conversion", "stalled branch"],
    lens: "sankey",
    build: (): SankeyDatum => ({
      title: "Content Flywheel Throughput",
      nodes: [
        { id: "podcasts", label: "Podcast transcripts", cluster: "source" },
        { id: "notes", label: "Obsidian notes", cluster: "source" },
        { id: "meetings", label: "Meeting recordings", cluster: "source" },
        { id: "synth", label: "Synthesis", cluster: "synthesis" },
        { id: "review", label: "Editorial review", cluster: "review" },
        { id: "social", label: "Social posts", cluster: "publish" },
        { id: "longform", label: "Longform essays", cluster: "publish" },
        { id: "archive", label: "Archive", cluster: "publish" },
        { id: "stalled", label: "Stalled drafts", cluster: "stalled" },
      ],
      links: [
        { source: "podcasts", target: "synth", value: 42 },
        { source: "notes", target: "synth", value: 64 },
        { source: "meetings", target: "synth", value: 28 },
        { source: "synth", target: "review", value: 96 },
        { source: "synth", target: "stalled", value: 38 },
        { source: "review", target: "social", value: 54 },
        { source: "review", target: "longform", value: 26 },
        { source: "review", target: "archive", value: 16 },
      ],
    }),
  },
  {
    id: "sankey-agent-token-flow",
    title: "Agent Token Flow",
    blurb:
      "An orchestrator's token budget fans out to subagents and on into the tools and outputs they drive.",
    highlights: ["token budget", "subagents", "tool spend"],
    lens: "sankey",
    build: (): SankeyDatum => ({
      title: "Agent Token Flow",
      nodes: [
        { id: "budget", label: "Orchestrator budget", cluster: "orchestrator" },
        { id: "researcher", label: "Researcher", cluster: "subagent" },
        { id: "coder", label: "Coder", cluster: "subagent" },
        { id: "reviewer", label: "Reviewer", cluster: "subagent" },
        { id: "search", label: "Web search", cluster: "tool" },
        { id: "filesys", label: "File ops", cluster: "tool" },
        { id: "exec", label: "Code exec", cluster: "tool" },
        { id: "artifact", label: "Artifacts", cluster: "output" },
        { id: "report", label: "Report", cluster: "output" },
      ],
      links: [
        { source: "budget", target: "researcher", value: 120 },
        { source: "budget", target: "coder", value: 180 },
        { source: "budget", target: "reviewer", value: 70 },
        { source: "researcher", target: "search", value: 84 },
        { source: "researcher", target: "report", value: 36 },
        { source: "coder", target: "filesys", value: 96 },
        { source: "coder", target: "exec", value: 84 },
        { source: "reviewer", target: "report", value: 44 },
        { source: "reviewer", target: "artifact", value: 26 },
        { source: "filesys", target: "artifact", value: 96 },
        { source: "exec", target: "artifact", value: 84 },
        { source: "search", target: "report", value: 84 },
      ],
    }),
  },
  {
    id: "sankey-session-value-flow",
    title: "Session Value Flow",
    blurb:
      "Raw working-session inputs distill into insights, then into the decisions and artifacts that outlast the session.",
    highlights: ["inputs", "insights", "decisions"],
    lens: "sankey",
    build: (): SankeyDatum => ({
      title: "Session Value Flow",
      nodes: [
        { id: "chat", label: "Chat threads", cluster: "input" },
        { id: "docs", label: "Reference docs", cluster: "input" },
        { id: "code", label: "Code reads", cluster: "input" },
        { id: "insights", label: "Insights", cluster: "insight" },
        { id: "noise", label: "Discarded", cluster: "noise" },
        { id: "decisions", label: "Decisions", cluster: "outcome" },
        { id: "artifacts", label: "Artifacts", cluster: "outcome" },
        { id: "followups", label: "Follow-ups", cluster: "outcome" },
      ],
      links: [
        { source: "chat", target: "insights", value: 58 },
        { source: "chat", target: "noise", value: 22 },
        { source: "docs", target: "insights", value: 46 },
        { source: "code", target: "insights", value: 70 },
        { source: "code", target: "noise", value: 14 },
        { source: "insights", target: "decisions", value: 62 },
        { source: "insights", target: "artifacts", value: 78 },
        { source: "insights", target: "followups", value: 34 },
      ],
    }),
  },
];
